import { Component, NgZone, OnInit, ViewChild, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { EbmsVeeyaaConfig } from '../../../app-config';
import { Location } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { NavigationItem } from './navigation/navigation';
import { NavigationStateService } from '../../../services/navigation-state.service';
import { AuthService } from '../../../services/auth.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit, OnDestroy {
  public nextConfig: any;
  public navCollapsed: boolean;
  public windowWidth: number;
  public navigationItems: any[] = [];
  public isMobileMenuOpen: boolean = false;
  private privilegesSubscription: Subscription | null = null;
  private routerSubscription: Subscription | null = null;
  private isRefreshingMenu: boolean = false; // Flag to prevent circular calls

  constructor(
    private zone: NgZone, 
    private location: Location, 
    private navigationItem: NavigationItem,
    private navigationStateService: NavigationStateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.nextConfig = EbmsVeeyaaConfig.config;
    let currentURL = this.location.path();
    const baseHerf = this.location['_baseHref'];
    if (baseHerf) {
      currentURL = baseHerf + this.location.path();
    }

    this.windowWidth = window.innerWidth;

    if (currentURL === baseHerf + '/layout/collapse-menu'
      || currentURL === baseHerf + '/layout/box'
      || (this.windowWidth >= 992 && this.windowWidth <= 1024)) {
      this.nextConfig.collapseMenu = true;
    }

    this.navCollapsed = (this.windowWidth >= 992) ? this.nextConfig.collapseMenu : false;
    
    // Don't set up subscriptions in constructor - wait for ngOnInit
    // This prevents errors when component tries to access view before it's ready
  }

  ngOnInit() {
    // Initialize collapsed state from service
    this.navCollapsed = this.navigationStateService.isDesktopCollapsed;
    this.isMobileMenuOpen = this.navigationStateService.isMobileMenuOpen;
    
    // Apply initial body classes
    if (this.navCollapsed) {
      document.body.classList.add('nav-collapsed');
    }
    
    // Only add mobile-menu-open class if actually on mobile device
    const isMobile = window.innerWidth < 992;
    if (isMobile && this.isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      // Remove if accidentally added on desktop
      document.body.classList.remove('mobile-menu-open');
    }
    
    // Set up subscriptions after view is initialized
    // This ensures the component is ready before subscriptions fire
    this.setupPrivilegesSubscription();
    this.setupRouterSubscription();
    
    // Initial menu load - only call once with token refresh
    // The subscription will handle updates when privileges are loaded
    // Use setTimeout to ensure view is fully initialized
    setTimeout(() => {
      if (!this.isRefreshingMenu) {
        this.refreshMenuFromSession(false);
      }
    }, 0);
  }

  /**
   * Refresh menu from session/localStorage
   * This is a public method that can be called to force refresh the menu
   * Checks both localStorage and AuthService for privileges
   * Similar to how dashboard component calls APIs in ngOnInit
   * @param skipTokenRefresh - If true, skip refreshing from token (prevents circular calls)
   */
  public refreshMenuFromSession(skipTokenRefresh: boolean = false): void {
    // Prevent recursive calls
    if (this.isRefreshingMenu) {
      return;
    }
    
    this.isRefreshingMenu = true;
    
    try {
      // Only refresh from token if explicitly requested (not from subscription)
      if (!skipTokenRefresh) {
        // First, refresh privileges from JWT token (like dashboard calls APIs)
        // This ensures we always have the latest privileges from the token
        this.authService.refreshPrivilegesFromToken();
      }
      
      let privileges: string[] = [];
      
      // Then check AuthService (from observable)
      const privilegesFromService = this.authService.getPrivileges();
      if (privilegesFromService && privilegesFromService.length > 0) {
        privileges = privilegesFromService;
      } else {
        // If not in service, check localStorage (for page reloads or async loading)
        const storedPrivileges = localStorage.getItem('user_privileges');
        if (storedPrivileges) {
          try {
            const parsedPrivileges = JSON.parse(storedPrivileges);
            if (Array.isArray(parsedPrivileges) && parsedPrivileges.length > 0) {
              privileges = parsedPrivileges;
            }
          } catch (error) {
            console.error('Error parsing stored privileges:', error);
          }
        }
      }
      
      // If we have privileges, load navigation
      if (privileges.length > 0) {
        this.loadNavigation();
        this.cdr.detectChanges();
      } else {
        // No privileges, clear navigation
        this.navigationItems = [];
        this.cdr.detectChanges();
      }
    } finally {
      // Reset flag after a short delay to allow for async operations
      setTimeout(() => {
        this.isRefreshingMenu = false;
      }, 100);
    }
  }

  /**
   * Check privileges from localStorage/session and load navigation if available
   * This is called on ngOnInit to handle page reloads and initial load
   */
  private checkPrivilegesAndLoadNavigation(): void {
    // First check if privileges are already in AuthService (from observable)
    const privilegesFromService = this.authService.getPrivileges();
    
    if (privilegesFromService && privilegesFromService.length > 0) {
      // Privileges are in service, load navigation immediately
      this.loadNavigation();
      this.cdr.detectChanges();
      return;
    }
    
    // If not in service, check localStorage (for page reloads)
    const storedPrivileges = localStorage.getItem('user_privileges');
    if (storedPrivileges) {
      try {
        const privileges = JSON.parse(storedPrivileges);
        if (Array.isArray(privileges) && privileges.length > 0) {
          // Privileges exist in localStorage, load navigation
          // Also ensure AuthService has them (in case it wasn't initialized)
          if (this.authService.isAuthenticated()) {
            this.loadNavigation();
            this.cdr.detectChanges();
          }
        }
      } catch (error) {
        console.error('Error parsing stored privileges:', error);
      }
    }
  }

  /**
   * Set up router subscription to detect navigation
   * This runs on every route change, ensuring menu is refreshed even if ngOnInit is not called
   */
  private setupRouterSubscription(): void {
    // Clean up existing subscription if any
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }
    
    // Subscribe to router events - this fires on EVERY navigation
    // This ensures menu is refreshed even if component is reused and ngOnInit is not called
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Only refresh if not already refreshing to prevent multiple calls
        if (!this.isRefreshingMenu) {
          // Skip token refresh to prevent circular calls (privileges should already be loaded)
          this.refreshMenuFromSession(true);
        }
        
        // Check if navigating to dashboard (after login)
        const isDashboardRoute = event.urlAfterRedirects.includes('/dashboard') || 
                                  event.urlAfterRedirects === '/dashboard' ||
                                  event.urlAfterRedirects.startsWith('/dashboard');
        
        if (isDashboardRoute && !this.isRefreshingMenu) {
          // Navigating to dashboard - wait for privileges to be set and load menu
          this.waitForPrivilegesAndLoadMenu();
        }
      });
  }

  /**
   * Wait for privileges to be set and then load menu
   * This is called when navigating to dashboard from login
   */
  private waitForPrivilegesAndLoadMenu(): void {
    // Only refresh once, with token refresh on first call
    if (!this.isRefreshingMenu) {
      this.refreshMenuFromSession(false);
    }
  }

  /**
   * Check if privileges are available and load menu
   */
  private checkAndLoadMenuIfPrivilegesAvailable(): void {
    const privileges = this.authService.getPrivileges();
    
    if (privileges && privileges.length > 0) {
      // Privileges are available, load navigation
      this.loadNavigation();
      this.cdr.detectChanges();
    } else {
      // Check localStorage as fallback
      const storedPrivileges = localStorage.getItem('user_privileges');
      if (storedPrivileges) {
        try {
          const parsedPrivileges = JSON.parse(storedPrivileges);
          if (Array.isArray(parsedPrivileges) && parsedPrivileges.length > 0) {
            // Privileges exist in localStorage, load navigation
            this.loadNavigation();
            this.cdr.detectChanges();
          }
        } catch (error) {
          console.error('Error parsing stored privileges:', error);
        }
      }
    }
  }

  /**
   * Set up privileges subscription
   * This subscribes to privileges observable and updates navigationItems when privileges change
   * Login page will set privileges in the observable, and we react here
   */
  private setupPrivilegesSubscription(): void {
    // Clean up existing subscription if any
    if (this.privilegesSubscription) {
      this.privilegesSubscription.unsubscribe();
      this.privilegesSubscription = null;
    }
    
    // Subscribe to privileges observable - BehaviorSubject will emit current value immediately
    // Use setTimeout to defer the subscription callback to avoid view initialization errors
    this.privilegesSubscription = this.authService.privileges$.subscribe((privileges) => {
      // Defer execution to next tick to ensure view is ready
      setTimeout(() => {
        if (privileges && privileges.length > 0) {
          // Privileges are available, refresh menu from session to ensure it's loaded
          // Skip token refresh to prevent circular calls (privileges already updated)
          if (!this.isRefreshingMenu) {
            this.refreshMenuFromSession(true);
          }
        } else {
          // Privileges are empty (logout happened), clear local variable
          this.navigationItems = [];
          this.cdr.detectChanges();
        }
      }, 0);
    });
  }




  toggleNavCollapse() {
    // Use service to manage collapsed state
    this.navigationStateService.toggleDesktopCollapsed();
    this.navCollapsed = this.navigationStateService.isDesktopCollapsed;
    
    // Add/remove body class for collapsed navigation
    if (this.navCollapsed) {
      document.body.classList.add('nav-collapsed');
    } else {
      document.body.classList.remove('nav-collapsed');
    }
  }

  toggleMobileMenu() {
    // Only toggle mobile menu on actual mobile devices
    const isMobile = window.innerWidth < 992;
    
    if (!isMobile) {
      // On desktop, remove the class if it exists and return
      document.body.classList.remove('mobile-menu-open');
      this.isMobileMenuOpen = false;
      return;
    }
    
    // Use service to manage mobile menu state
    this.navigationStateService.toggleMobileMenu();
    this.isMobileMenuOpen = this.navigationStateService.isMobileMenuOpen;
    
    // Add/remove body class for mobile menu (only on mobile)
    if (this.isMobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    
  }

  /**
   * Load navigation items (filtered by privileges)
   */
  private loadNavigation(): void {
    try {
      const items = this.navigationItem.get();
      // Ensure we always have an array, even if get() returns null/undefined
      if (Array.isArray(items) && items.length > 0) {
        this.navigationItems = items;
      } else {
        // Set to empty array if no items
        this.navigationItems = [];
      }
    } catch (error) {
      console.error('Error loading navigation:', error);
      // Fallback to empty array to prevent template errors
      this.navigationItems = [];
    }
    // Trigger change detection after loading navigation
    this.cdr.detectChanges();
  }


  ngOnDestroy(): void {
    // Unsubscribe from privileges observable
    if (this.privilegesSubscription) {
      this.privilegesSubscription.unsubscribe();
      this.privilegesSubscription = null;
    }
    
    // Unsubscribe from router events
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
      this.routerSubscription = null;
    }
  }
}
